import { Emitter } from "@core/events/emitter";

/**
 * `IUndoRedo` — port of Unity's `IUndoRedo` (`Models/IEditorModel.cs`,
 * `Models/UndoRedo.cs`). The web implementation is a plain command stack;
 * Battlehub's `IRTE` integration is dropped because we don't ship Unity
 * runtime editor.
 *
 * Semantics that match Unity:
 *
 * - `BeginRecordGroup` / `EndRecordGroup` collapse a burst of `record`
 *   calls into a single undoable step.
 * - `IsRecording` flips off while we are running an undo / redo so commands
 *   don't re-enter the stack.
 * - `BeforeUndo` / `AfterUndo` / `BeforeRedo` / `AfterRedo` / `StateChanged`
 *   are emitted to keep UI buttons (`HeaderView`, context menus) live.
 */
export interface UndoRedoCommand {
  /** Run forward (initial action and re-execution on redo). */
  readonly redo: () => void;
  /** Reverse the action. */
  readonly undo: () => void;
  /** Optional debug label surfaced in observability + logs. */
  readonly label?: string;
}

export interface IUndoRedo {
  readonly onBeforeUndo: Emitter<void>;
  readonly onAfterUndo: Emitter<void>;
  readonly onBeforeRedo: Emitter<void>;
  readonly onAfterRedo: Emitter<void>;
  readonly onStateChanged: Emitter<void>;

  readonly isRecording: boolean;
  readonly canUndo: boolean;
  readonly canRedo: boolean;

  /** Push a command on the stack and execute its `redo` once. No-op when
   *  `isRecording === false` (i.e. while another undo/redo is replaying). */
  record(command: UndoRedoCommand): void;

  /** Open a group; subsequent `record` calls collapse into one entry until
   *  the matching `endRecordGroup` runs. Groups can be nested. */
  beginRecordGroup(label?: string): void;
  endRecordGroup(): void;

  undo(): void;
  redo(): void;

  /** Drop both stacks (e.g. when switching scenes — see Unity
   *  `ProjectModel_ScenesManagement.cs::SetActiveScene`). */
  clear(): void;
}

export const UNDO_REDO = Symbol.for("Campfire.UndoRedo");
